/**
 * A recorded video file standing in for the camera.
 *
 * Reviewing form live is awkward: the athlete is the one holding the phone, the
 * numbers go past at 60fps, and a rep you want to look at twice is gone. Running
 * the same pipeline over a file fixes all three — and because `detectForVideo`
 * takes any `ImageSource`, nothing about the detection path has to change.
 *
 * Two things differ from the camera and both matter:
 *
 * - **No mirror.** The camera image is mirrored so the athlete sees themselves
 *   as in a mirror, and the renderer bakes that into the 2D context. A recording
 *   is already the right way round, so the renderer is built unmirrored here.
 * - **Media time, not wall-clock time.** Timestamps come from `currentTime`, so
 *   the analysis is in the video's own clock. That makes it independent of
 *   playback rate and of how long inference takes, which wall-clock timestamps
 *   would smear.
 *
 * It also means the whole app runs with no camera at all, which is the only way
 * to exercise this pipeline in a sandbox or in CI.
 */

/** Long enough for a slow decode of a large file, short enough to fail visibly. */
const METADATA_TIMEOUT_MS = 20000;

/**
 * Why a file can be selectable and still not play.
 *
 * The input accepts `video/*`, which is about the container, while playing is
 * about the codec inside it. A phone recording is the common case: an iPhone
 * writes `.mov`, which Safari plays natively on every device, and which Chrome
 * plays only when the track inside is H.264 — a clip recorded in "High
 * Efficiency" mode is HEVC and Chrome may refuse it on the same machine where
 * Safari is fine. Nothing can be done about that in a page, so the message says
 * which browser to open instead rather than blaming the file.
 */
function decodeHint(file) {
  const name = (file && file.name) || "";
  const type = (file && file.type) || "";
  const quicktime = /\.mov$/i.test(name) || type === "video/quicktime";
  if (quicktime) {
    return (
      "Could not decode that video. A .mov from an iPhone plays in Safari " +
      "but not always in Chrome, which needs H.264 rather than HEVC."
    );
  }
  return "Could not decode that video. Try an MP4 the browser can play natively.";
}

function waitForMetadata(video, timeoutMs, file) {
  if (video.videoWidth > 0 && video.videoHeight > 0) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(deadline);
      video.removeEventListener("loadedmetadata", onReady);
      video.removeEventListener("error", onError);
    };
    const onReady = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(decodeHint(file)));
    };
    const deadline = setTimeout(() => {
      cleanup();
      reject(new Error("Timed out reading the video."));
    }, timeoutMs);
    video.addEventListener("loadedmetadata", onReady);
    video.addEventListener("error", onError);
  });
}

export class VideoFile {
  constructor() {
    this.video = document.getElementById("video");
    this.objectUrl = null;
  }

  /** True for a source whose image is already the right way round. */
  get mirrored() {
    return false;
  }

  /**
   * Timestamps in milliseconds on the video's own clock, which is what makes
   * the analysis independent of playback rate and of inference speed.
   */
  timestamp() {
    return this.video.currentTime * 1000;
  }

  static async setup(file) {
    const source = new VideoFile();
    const video = source.video;

    // A camera stream may still be attached if the mode was switched at runtime.
    if (video.srcObject) {
      for (const track of video.srcObject.getTracks()) {
        track.stop();
      }
      video.srcObject = null;
    }

    source.objectUrl = URL.createObjectURL(file);
    video.src = source.objectUrl;
    video.muted = true;
    video.playsInline = true;
    video.loop = false;
    // The camera path sets this; a file must not inherit it, or scrubbing back
    // to look at a rep again would restart the whole analysis.
    video.autoplay = false;
    video.load();

    await waitForMetadata(video, METADATA_TIMEOUT_MS, file);

    video.width = video.videoWidth;
    video.height = video.videoHeight;
    const wrapper = document.querySelector(".canvas-wrapper");
    wrapper.style.aspectRatio = `${video.videoWidth} / ${video.videoHeight}`;

    return source;
  }

  play() {
    const playing = this.video.play();
    if (playing && typeof playing.catch === "function") {
      playing.catch(() => {});
    }
  }

  release() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
